import clsx from 'clsx'
import {Dispatch, FC, Fragment, ReactNode, SetStateAction, useEffect, useState} from 'react'
import {InputHint} from './InputHint'
import {PersianDatePicker} from './datepicker/PersianDatePicker'
import {
  API_URL,
  fixNumbers,
  fixZero,
  isNotEmpty,
  isAPP,
  numberWithCommas,
} from '../crud-helper/helpers'
import {SearchSelect, SearchSelectOption} from './SearchSelect'
import {Uploader, UploaderProps} from './Uploader'
import {openAttachmentFile} from './AttachmentFileAccess'
import OTPInput from 'react-otp-input'
import {amountInWords} from '../../../app/modules/apps/shared/amount/amountText'
import {NumericFormat} from 'react-number-format'
import {emulateTab} from 'emulate-tab'
import {FieldHelpLabel, FieldHelpOptionIcon} from './FieldHelp'

type props = {
  type:
    | 'text'
    | 'textarea'
    | 'datepicker'
    | 'toggle'
    | 'number'
    | 'currency'
    | 'select'
    | 'uploader'
    | 'radio'
    | 'multiselect'
    | 'otp'
    | 'checkbox'
  label?: ReactNode
  labelAccessory?: ReactNode
  value?: any
  isDisabled?: boolean
  isReadonly?: boolean
  isRequired?: boolean
  placeholder?: string
  items?: SearchSelectOption[]
  error?: ReactNode
  hint?: ReactNode
  stateValue?: {value: any; setValue: Dispatch<SetStateAction<any>>}
  toggleValue?: {
    off: string
    on: string
  }
  LTR?: boolean
  onKeyDownFn?: (e: any) => void
  limitFromDate?: string
  limitToDate?: string
  timePicker?: boolean
  uploaderProps?: UploaderProps
  style?: React.CSSProperties
  inputId?: string
  children?: React.ReactNode
  inputClass?: string
  textAreaRows?: number
  isVertical?: boolean
  max?: number
  onBlurFn?: () => void
  percentOf?: number
  percentDefault?: string
  numberAsText?: boolean
  /** Text input that keeps only Latin digits: Persian/Arabic digits are converted, anything else is dropped. */
  digitsOnly?: boolean
  isPassword?: boolean
  onPercent?: boolean
  hasDownload?: boolean
  helpKey?: string
  /** A text field's keyboard and autofill, e.g. 'tel' for a phone number (507); digitsOnly already gives 'numeric'. */
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  autoComplete?: string
}

const CustomInputs: FC<props> = ({
  type,
  label,
  labelAccessory,
  isDisabled,
  isRequired = false,
  placeholder,
  error,
  stateValue,
  hint: __hint,
  toggleValue,
  items,
  isReadonly = false,
  value: _value = null,
  LTR = false,
  onKeyDownFn: _onKeyDownFn,
  onBlurFn,
  limitFromDate,
  limitToDate,
  timePicker = false,
  uploaderProps,
  style,
  inputId,
  children,
  inputClass,
  max,
  percentOf,
  textAreaRows = 3,
  isVertical = false,
  numberAsText = false,
  digitsOnly = false,
  isPassword = false,
  percentDefault,
  onPercent: _onPercent = true,
  hasDownload = false,
  helpKey,
  inputMode,
  autoComplete,
}) => {
  const [hint, setHint] = useState<ReactNode>(__hint)
  const [first, setFirst] = useState(true)
  const [firstPercent, setFirstPercent] = useState(true)
  const [id, setId] = useState<string | number>(inputId ?? 0)
  // The date field's label names its input.
  const datePickerId = inputId || `mp-dp-field-${id}`
  const [onPercent, setOnPercent] = useState(_onPercent && percentOf != undefined)
  const [percent, setPercent] = useState(percentDefault || '')
  const [updKey, setUpdKey] = useState(1)
  const [value, setValue] = useState(_value ? JSON.parse(JSON.stringify(_value)) : undefined)

  useEffect(() => {
    if (first) {
      setFirst(false)
      if (!inputId) {
        const _id = Math.floor(Math.random() * 1000000)
        setId(_id)
      }
      seprateNumber()
    }
  }, [])

  useEffect(() => {
    seprateNumber()
  }, [stateValue?.value, percentOf, percent, value])

  const onKeyDownFn = (e: any) => {
    if (_onKeyDownFn) {
      _onKeyDownFn(e)
    } else {
      if (e.keyCode == 13) {
        emulateTab()
      }
    }
  }

  useEffect(() => {
    if (onPercent && _onPercent && percentOf != undefined && Number(percentOf) > 0) {
      if (
        stateValue &&
        isNotEmpty(percentOf) &&
        Number(percentOf) > 0 &&
        (!isRequired || (isRequired && isNotEmpty(percent)))
      ) {
        const res = Math.round((Number(percentOf) / 100) * Number(percent))
        setTimeout(() => {
          stateValue.setValue(res.toString())
        }, 1)
      } else {
        if (firstPercent) {
          setPercent(isNotEmpty(percentDefault) ? percentDefault || '' : '')
        }
      }
    }
    seprateNumber()
  }, [percent, percentOf, stateValue?.value])

  useEffect(() => {
    seprateNumber()
  }, [onPercent && _onPercent, percentOf, percent])

  const horoof = (val: any) => {
    try {
      if (val && val != null && isNotEmpty(val)) {
        let _val = parseFloat(val.toString().split('.')[0])
        if (_val && _val != null && isNotEmpty(_val)) {
          return amountInWords(_val)
        } else {
          return ''
        }
      } else {
        return ''
      }
    } catch (error) {
      return ''
    }
  }

  const seprateNumber = () => {
    if (
      (stateValue &&
        percentOf != undefined &&
        isNotEmpty(percentOf) &&
        Number(percentOf) > 0 &&
        !(onPercent && _onPercent)) ||
      (firstPercent &&
        onPercent &&
        _onPercent &&
        !isNotEmpty(percent) &&
        isNotEmpty(stateValue?.value) &&
        stateValue?.value != '0')
    ) {
      setPercent(
        Math.round((Number(stateValue?.value.toString()) / (percentOf || 1)) * 100).toString()
      )
    } else if (onPercent && _onPercent && !isNotEmpty(percent)) {
      if (firstPercent) {
        setPercent(percentDefault != undefined ? percentDefault : '')
      }
      if (!firstPercent) {
        if (stateValue) {
          setTimeout(() => {
            stateValue.setValue('')
          }, 1)
        }
      }
    }
    if (firstPercent && !isDisabled) {
      setTimeout(() => {
        setFirstPercent(false)
      }, 500)
    }
    let val = (stateValue ? stateValue.value : value) || ''
    if (val == 'NaN') val = ''
    setHint(
      <>
        {__hint}
        {percentOf != undefined ? (
          <>
            {onPercent && _onPercent ? (
              <>
                {numberWithCommas(stateValue?.value)}
                <span className='ms-5'>{<>{horoof(val)}</>}</span>{' '}
                {type == 'currency' && (
                  <>
                    <span className='text-gray-600 ms-1'>ریال</span>
                  </>
                )}
              </>
            ) : (
              <>
                %{numberWithCommas(Number(percent))}
                <span className='ms-5'>{<>{horoof(val)}</>}</span>{' '}
                {type == 'currency' && (
                  <>
                    <span className='text-gray-600 ms-1'>ریال</span>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <>
            {type == 'currency' && (
              <>
                {horoof(val)} <span className='text-gray-600 ms-1'>ریال</span>
              </>
            )}
          </>
        )}
      </>
    )
  }

  useEffect(() => {
    if (isAPP) {
      const inputElement = document.getElementById(id.toString())
      if (inputElement) {
        inputElement.onfocus = () => {
          inputElement.scrollIntoView()
          setTimeout(() => {
            var elm = document.getElementById('kt_modal_add_invoice_item_body')
            if (elm) {
              elm.scrollTop = elm.scrollTop - 28
            }
          }, 1)
        }
      }
    }
  }, [id])

  useEffect(() => {
    if (type == 'select' && items && items.filter((x) => !x.disabled).length == 1 && stateValue) {
      stateValue.setValue(items[0].value)
    }
  }, [items])

  const getUploaderFileUrl = (source: string) => {
    if (/^https?:\/\//i.test(source)) {
      return source
    }

    if (source.startsWith('/api/')) {
      return `${API_URL}${source.substring(4)}`
    }

    if (source.startsWith('/ticketing/') || source.startsWith('/file-library/')) {
      return `${API_URL}${source}`
    }

    return `${API_URL}/upload?load=${encodeURIComponent(source)}`
  }

  const safeDecodeURIComponent = (value: string) => {
    try {
      return decodeURIComponent(value)
    } catch (error) {
      return value
    }
  }

  const getUploaderFileInfo = (file: any) => {
    const source = (file?.source || '').toString()
    const meta = file?.options?.file || {}
    const sourcePath = source.split('?')[0].split('#')[0]
    const fallbackName = safeDecodeURIComponent(
      sourcePath.split('/').filter(Boolean).pop() || 'attachment'
    )
    const name = meta.name || fallbackName
    const type = meta.type || ''
    const isImage =
      type.toString().startsWith('image/') ||
      /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name.toString())

    return {
      name,
      source,
      type,
      isImage,
      url: getUploaderFileUrl(source),
    }
  }

  const checkedValue = (itm: SearchSelectOption) => {
    try {
      const isChecked =
        stateValue &&
        stateValue.value &&
        stateValue.value != null &&
        typeof stateValue.value == 'object' &&
        stateValue.value.length > 0 &&
        (stateValue.value as SearchSelectOption[]).findIndex((x) => x.value == itm.value) > -1
      if (!isChecked && value) {
        try {
          const valInd =
            value && value.length > 0 ? (value as number[]).findIndex((x) => x == itm.value) : -1
          if (valInd > -1) {
            setTimeout(() => {
              CheckboxUpdate(itm)
            }, 1)
            setTimeout(() => {
              let newValue = value as number[]
              newValue = newValue.splice(valInd, 1)
              setValue(newValue)
            }, 100)
            return true
          }
        } catch (error) {}
      }
      return isChecked
    } catch (error) {
      return false
    }
  }

  const CheckboxUpdate = (itm: SearchSelectOption) => {
    if (stateValue && stateValue.value) {
      try {
        let val: SearchSelectOption[] = JSON.parse(
          JSON.stringify(stateValue.value)
        ) as SearchSelectOption[]
        if (val.findIndex((x) => x.value == itm.value) > -1) {
          val.splice(
            val.findIndex((x) => x.value == itm.value),
            1
          )
        } else {
          val.push(itm)
        }
        stateValue.setValue(val)
      } catch (error) {
        stateValue && stateValue.setValue([itm])
      }
    }
  }

  // A value loaded with other characters (an older invoice) is flagged; typing cleans it.
  const textValue = stateValue ? stateValue.value : value
  const digitsOnlyError =
    digitsOnly && type == 'text' && isNotEmpty(textValue) && !/^\d+$/.test(String(textValue))
      ? 'فقط رقم وارد کنید؛ حروف و نشانه‌ها پذیرفته نمی‌شوند.'
      : undefined

  return (
    <>
      {label && type != 'uploader' && (
        <>
          <label
            key={updKey}
            className={clsx('fw-bold fs-6 mb-2')}
            htmlFor={type === 'datepicker' ? datePickerId : undefined}
          >
            {type == 'toggle' && isVertical ? (
              <>
                <span className='form-check form-check-custom form-check-primary form-check-solid form-check-sm mb-5'>
                  <input
                    name={id.toString()}
                    className='form-check-input'
                    type='checkbox'
                    disabled={isDisabled}
                    id={`${id}`}
                    checked={
                      (stateValue && stateValue.value != null ? stateValue.value : value) == true
                    }
                    onChange={() => {
                      if (stateValue) {
                        if (stateValue.value == true) {
                          stateValue.setValue(false)
                        } else {
                          stateValue.setValue(true)
                        }
                      }
                    }}
                  />
                  <label
                    className={clsx(
                      'form-check-label',
                      (stateValue ? stateValue.value : value) == true
                        ? 'text-gray-700 fw-bolder'
                        : 'text-gray-500'
                    )}
                    htmlFor={`${id}`}
                  >
                    <FieldHelpLabel label={label} helpKey={helpKey} isRequired={isRequired} />{' '}
                    {isRequired && <label title='ضروری' className='fw-bold fs-6 required'></label>}
                  </label>
                </span>
              </>
            ) : (
              <>
                <FieldHelpLabel label={label} helpKey={helpKey} isRequired={isRequired} />{' '}
                {isRequired && <label title='ضروری' className='fw-bold fs-6 required'></label>}
              </>
            )}
          </label>
          {labelAccessory}
          <br />
        </>
      )}
      {/* <div className={clsx({row: children}, {'w-100': !children})}> */}
      <div className={clsx('input-group')}>
        {/* <div className={clsx({'col-lg-8 col-md-9 col-sm-12': children}, {'w-100': !children})}> */}
        {type == 'textarea' && (
          <textarea
            readOnly={isReadonly}
            rows={textAreaRows}
            className={clsx(
              'form-control form-control-solid mb-3 mb-lg-0',
              {'text-end': LTR},
              inputClass
            )}
            autoComplete='off'
            disabled={isDisabled}
            value={stateValue ? stateValue.value : value}
            onChange={(e) => {
              stateValue && stateValue.setValue(e.target.value)
            }}
          />
        )}
        {type == 'text' && (
          <input
            readOnly={isReadonly}
            onWheel={(e) => e.currentTarget.blur()}
            placeholder={placeholder}
            onKeyDown={onKeyDownFn}
            onBlur={onBlurFn}
            type={isPassword ? 'password' : 'text'}
            style={style}
            className={clsx(
              'form-control form-control-solid mb-3 mb-lg-0',
              {'text-end ltr': LTR},
              inputClass
            )}
            autoComplete={autoComplete || 'off'}
            disabled={isDisabled}
            inputMode={inputMode || (digitsOnly ? 'numeric' : undefined)}
            // A digits-only paste is cut after cleaning, so dropped letters do not cost digits.
            maxLength={digitsOnly ? undefined : max}
            value={stateValue ? stateValue.value : value}
            onChange={(e) => {
              const next = digitsOnly
                ? fixNumbers(e.target.value).replace(/\D/g, '').slice(0, max)
                : e.target.value
              percentOf != undefined && onPercent && _onPercent
                ? setPercent(next)
                : stateValue && stateValue.setValue(next)
            }}
          />
        )}
        {(type == 'number' || type == 'currency') && (
          <>
            {numberAsText ? (
              <input
                readOnly={isReadonly}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder={placeholder}
                onKeyDown={onKeyDownFn}
                onBlur={onBlurFn}
                type={'number'}
                style={style}
                className={clsx(
                  'form-control form-control-solid mb-3 mb-lg-0',
                  'text-end ltr',
                  inputClass
                )}
                autoComplete='off'
                disabled={isDisabled}
                maxLength={(onPercent && _onPercent ? 6 : max) || 19}
                value={
                  percentOf != undefined && onPercent && _onPercent
                    ? percent
                    : stateValue
                    ? stateValue.value
                    : value
                }
                onChange={(e) => {
                  percentOf != undefined && onPercent && _onPercent
                    ? setPercent(e.target.value)
                    : stateValue && stateValue.setValue(e.target.value)
                }}
              />
            ) : (
              <NumericFormat
                dir='ltr'
                id={id.toString()}
                thousandSeparator=','
                allowLeadingZeros={false}
                allowNegative={false}
                readOnly={isReadonly}
                onWheel={(e: any) => e.currentTarget.blur()}
                placeholder={placeholder}
                onKeyDown={onKeyDownFn}
                onBlur={onBlurFn}
                type={'text'}
                style={style}
                autoComplete='off'
                disabled={isDisabled}
                maxLength={(onPercent && _onPercent ? 6 : max) || 19}
                value={
                  percentOf != undefined && onPercent && _onPercent
                    ? percent
                    : stateValue
                    ? stateValue.value
                    : value
                }
                className={clsx(
                  'form-control form-control-solid mb-3 mb-lg-0 fs-3',
                  'text-end',
                  inputClass
                )}
                onValueChange={(values, sourceInfo) => {
                  percentOf != undefined && onPercent && _onPercent
                    ? setPercent(values.value)
                    : stateValue && stateValue.setValue(values.value)
                }}
              />
            )}
            {percentOf != undefined && (
              <div className='input-group-prepend'>
                <span
                  className={clsx(
                    'form-control form-control-solid fs-3 px-5',
                    onPercent && _onPercent
                      ? 'text-success bg-light-success fw-bolder'
                      : 'text-gray-400'
                  )}
                  onClick={() => {
                    setOnPercent(!onPercent)
                  }}
                  style={{borderRight: 0, borderTopRightRadius: 0, borderBottomRightRadius: 0}}
                >
                  %
                </span>
              </div>
            )}
          </>
        )}
        {/* On card-reader hosts too: there the field is typed into and its button opens the calendar. */}
        {type == 'datepicker' && (
          <div className='w-100'>
            <PersianDatePicker
              id={datePickerId}
              value={stateValue ? stateValue.value : value}
              onChange={(next) => stateValue?.setValue(next)}
              minDate={limitFromDate}
              maxDate={limitToDate}
              timePicker={timePicker}
              readOnly={isReadonly}
              disabled={isDisabled}
              clearable={!isRequired}
              placeholder={placeholder}
            />
          </div>
        )}
        {type == 'toggle' && (
          <>
            {!isVertical && (
              <div
                className={clsx('form-check form-switch form-check-custom form-check-solid mt-4')}
              >
                <label
                  className={clsx(
                    'form-check-label',
                    `${
                      (stateValue && stateValue.value && stateValue.value == '1') ||
                      (value && value == '1')
                        ? 'text-gray-500 fw-lighter fs-6'
                        : 'text-gray-700 fw-bolder fs-5'
                    }`
                  )}
                >
                  {toggleValue?.off}
                </label>
                {toggleValue?.off && (
                  <FieldHelpOptionIcon
                    option={toggleValue.off}
                    fieldLabel={label}
                    helpKey={helpKey}
                    isRequired={isRequired}
                  />
                )}
                <input
                  readOnly={isReadonly}
                  disabled={isDisabled}
                  className={clsx(
                    'form-check-input h-20px w-30px mx-3',
                    (stateValue && stateValue.value && stateValue.value == '1') ||
                      (value && value == '1')
                      ? 'bg-success'
                      : 'bg-gray-400'
                  )}
                  type='checkbox'
                  value='1'
                  checked={
                    (stateValue && stateValue.value && stateValue.value == '1') ||
                    (value && value == '1')
                      ? true
                      : false
                  }
                  onChange={(e) => {
                    stateValue && stateValue.setValue(e.target.checked ? '1' : '0')
                  }}
                />
                <label
                  className={clsx(
                    'form-check-label',
                    `${
                      stateValue && stateValue.value && stateValue.value == '1'
                        ? 'text-success fw-bolder fs-5'
                        : 'text-gray-700 fw-lighter fs-6'
                    }`
                  )}
                >
                  {toggleValue?.on}
                </label>
                {toggleValue?.on && (
                  <FieldHelpOptionIcon
                    option={toggleValue.on}
                    fieldLabel={label}
                    helpKey={helpKey}
                    isRequired={isRequired}
                  />
                )}
              </div>
            )}
          </>
        )}
        {type == 'select' && (
          <>
            <select
              className={clsx('form-select form-select-solid')}
              placeholder={placeholder}
              disabled={isDisabled || isReadonly}
              value={stateValue ? stateValue.value : value}
              onChange={(e) => {
                stateValue && stateValue.setValue(e.target.value)
              }}
            >
              {/* Nothing chosen yet: the browser would show the first option as if it were (the proforma's fiscal
                  memory looked chosen and was refused, 2026-10-16); an empty select says so instead. */}
              {stateValue &&
                (stateValue.value === '' || stateValue.value === undefined || stateValue.value === null) &&
                !items?.some((item) => String(item.value) === '') && (
                  <option value='' disabled hidden>
                    {placeholder || 'انتخاب کنید'}
                  </option>
                )}
              {items?.map((item, ind) => {
                return (
                  <option
                    key={ind}
                    value={item.value}
                    disabled={item.disabled && item.disabled == true}
                  >
                    {item.label}
                  </option>
                )
              })}
            </select>
          </>
        )}
        {type == 'multiselect' && stateValue && (
          <div className='w-100'>
            <SearchSelect
              section=''
              stateValue={stateValue}
              items={items}
              defaultvalue={isNotEmpty(value) ? Number(value) : undefined}
              isDisabled={isDisabled || isReadonly}
              isMulti
            />
          </div>
        )}
        {type == 'uploader' && (
          <>
            {isDisabled && uploaderProps && uploaderProps.files ? (
              <ul className='list-unstyled mb-0 w-100'>
                {uploaderProps.files.map((f, i) => {
                  const fileInfo = getUploaderFileInfo(f)
                  return (
                    <li
                      key={`uploader-file-${i}-${fileInfo.url}`}
                      className='text-start w-100 mb-3'
                      style={{direction: 'ltr'}}
                    >
                      <button
                        type='button'
                        onClick={() => openAttachmentFile(fileInfo.source, fileInfo.name)}
                        className='btn btn-link d-inline-flex align-items-center gap-2 link-primary p-0 text-decoration-none'
                      >
                        <i className='las la-file-download fs-2x'></i>
                        <span>
                          {i + 1}. {fileInfo.name}
                        </span>
                      </button>
                      {fileInfo.isImage && !fileInfo.source.includes('/file-library/') && (
                        <div className='mt-2'>
                          <img
                            alt={fileInfo.name}
                            className='rounded border'
                            style={{maxWidth: '120px', maxHeight: '120px', objectFit: 'cover'}}
                            src={fileInfo.url}
                          />
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            ) : (
              <div className='w-100 d-block'>
                <Uploader
                  {...uploaderProps}
                  label={label ? label.toString() : undefined}
                  hasDownload={hasDownload}
                />
              </div>
            )}
          </>
        )}
        {type == 'radio' && items && (
          <div
            className={clsx(
              'd-flex justify-content-between',
              {'flex-column': isVertical},
              {'ms-5': isVertical}
            )}
          >
            {items.map((itm, ind) => {
              return (
                <span
                  key={ind}
                  className='form-check form-check-custom form-check-primary form-check-solid form-check-sm mb-5 me-3'
                >
                  <input
                    name={id.toString()}
                    className='form-check-input'
                    type='radio'
                    value={itm.value}
                    disabled={itm.disabled || isDisabled}
                    id={`${id}_${ind}`}
                    checked={(stateValue ? stateValue.value : value) == itm.value}
                    onChange={() => {
                      stateValue && stateValue.setValue(itm.value)
                    }}
                  />
                  <label
                    className={clsx(
                      'form-check-label',
                      (stateValue ? stateValue.value : value) == itm.value
                        ? 'text-gray-700 fw-bolder'
                        : 'text-gray-500'
                    )}
                    htmlFor={`${id}_${ind}`}
                  >
                    {itm.label}
                  </label>
                  <FieldHelpOptionIcon
                    option={itm.label}
                    fieldLabel={label}
                    helpKey={helpKey}
                    isRequired={isRequired}
                  />
                </span>
              )
            })}
          </div>
        )}
        {type == 'checkbox' && items && (
          <div
            key={updKey}
            className={clsx(
              'd-flex justify-content-between',
              {'flex-column': isVertical},
              {'ms-5': isVertical}
            )}
          >
            {items.map((itm, ind) => {
              return (
                <span
                  key={ind}
                  className='form-check form-check-custom form-check-primary form-check-solid form-check-sm mb-5'
                >
                  <input
                    name={id.toString()}
                    className='form-check-input'
                    type='checkbox'
                    disabled={isDisabled || itm.disabled}
                    value={itm.value}
                    id={`${id}_${ind}`}
                    checked={checkedValue(itm)}
                    onChange={() => {
                      setUpdKey(updKey + 1)
                    }}
                    onClick={() => {
                      CheckboxUpdate(itm)
                    }}
                  />
                  <label
                    className={clsx(
                      'form-check-label',
                      (stateValue ? stateValue.value : value) == itm.value
                        ? 'text-gray-700 fw-bolder'
                        : 'text-gray-500'
                    )}
                    htmlFor={`${id}_${ind}`}
                  >
                    {itm.label}
                  </label>
                  <FieldHelpOptionIcon
                    option={itm.label}
                    fieldLabel={label}
                    helpKey={helpKey}
                    isRequired={isRequired}
                  />
                </span>
              )
            })}
          </div>
        )}
        {type == 'otp' && stateValue && (
          <OTPInput
            containerStyle={{direction: 'ltr'}}
            value={stateValue.value}
            onChange={stateValue.setValue}
            numInputs={5}
            renderSeparator={<>-</>}
            renderInput={(props) => (
              <input
                id='otpCode'
                {...props}
                className={clsx('form-control bg-transparent text-center')}
                disabled={isDisabled}
                autoComplete='off'
                style={{direction: 'ltr'}}
              />
            )}
          />
        )}
        {/* </div> */}
        {children}
      </div>
      {(isNotEmpty(error) || isNotEmpty(hint) || digitsOnlyError) && (
        <InputHint hint={hint} error={isNotEmpty(error) ? error : digitsOnlyError} />
      )}
    </>
  )
}

export {CustomInputs}
